import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Utensils, Users, MapPin, Loader2, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import { breadcrumbLD, eventsItemListLD } from '../utils/structuredData';
import { cityName } from '../utils/seoFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /res — global reservations directory
 * /res/:city — city-scoped reservations directory
 *
 * Mirrors the /events/{city} directory shape so SEO crawlers see a
 * consistent ItemList + BreadcrumbList graph across all three product
 * kinds.
 */
export default function ReservationDirectory() {
  const { city: cityParam } = useParams();
  const cityNice = cityParam ? cityName(cityParam) : null;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [partySize, setPartySize] = useState('');

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (cityParam) params.set('city', cityParam);
    if (q.trim()) params.set('q', q.trim());
    if (partySize) params.set('party_size', partySize);
    params.set('limit', '60');
    axios.get(`${API}/reservations?${params}`)
      .then(res => setRows(res.data?.reservations || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [cityParam, q, partySize]);

  const title = cityNice ? `Restaurant reservations in ${cityNice}` : 'Restaurant reservations';
  const description = cityNice
    ? `Reserve a table at the best restaurants in ${cityNice}. Real-time availability, instant confirmation on Eventism.`
    : 'Reserve a table at top restaurants nationwide. Real-time availability + instant confirmation on Eventism.';

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://eventism.com';
  const itemList = useMemo(
    () => eventsItemListLD(
      rows.map(r => ({ ...r, _publicUrlPrefix: '/res' })),
      title,
      baseUrl,
    ),
    [rows, title, baseUrl]
  );
  const crumbs = breadcrumbLD(
    cityNice
      ? [
          { name: 'Home', url: '/' },
          { name: 'Reservations', url: '/res' },
          { name: cityNice, url: `/res/${cityParam}` },
        ]
      : [
          { name: 'Home', url: '/' },
          { name: 'Reservations', url: '/res' },
        ]
  );

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="reservation-directory">
      <SEO
        title={`${title} · Eventism`}
        description={description}
        keywords={[
          'restaurant reservations', 'book a table', 'dinner reservations',
          cityNice ? `restaurants in ${cityNice}` : 'restaurants near me',
          cityNice ? `${cityNice} reservations` : 'reservations near me',
        ].filter(Boolean)}
        structuredData={[itemList, crumbs]}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <Utensils className="h-3.5 w-3.5" />
            <span className="uppercase tracking-wider">Table reservations</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-3" data-testid="reservation-directory-title">
            {cityNice ? <>Reserve a table in <span className="text-amber-500">{cityNice}</span></> : <>Reserve your next <span className="text-amber-500">table</span></>}
          </h1>
          <p className="text-base text-muted-foreground max-w-2xl">{description}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-6">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search restaurants…"
              className="pl-9"
              data-testid="res-dir-search"
            />
          </div>
          <select
            value={partySize}
            onChange={e => setPartySize(e.target.value)}
            className="h-10 px-3 border rounded-md bg-background text-sm"
            data-testid="res-dir-party-size"
          >
            <option value="">Any party size</option>
            {[2, 3, 4, 5, 6, 8, 10, 12].map(n => <option key={n} value={n}>Party of {n}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground" data-testid="res-dir-empty">
            <Utensils className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No reservations found{cityNice ? ` in ${cityNice}` : ''}. New venues are added every week.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="res-dir-grid">
            {rows.map(r => <ReservationCard key={r.event_id} item={r} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function ReservationCard({ item }) {
  return (
    <Link
      to={`/res/${item.city}/${item.slug}`}
      className="group rounded-xl overflow-hidden border border-border bg-card hover:border-amber-500/60 hover:shadow-lg transition-all"
      data-testid={`res-card-${item.event_id}`}
    >
      <div className="aspect-video bg-muted overflow-hidden">
        {item.image_url ? (
          <img src={item.image_url} alt={item.venue_name || item.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full grid place-items-center text-muted-foreground"><Utensils className="h-10 w-10 opacity-40" /></div>
        )}
      </div>
      <div className="p-4 space-y-2">
        <h3 className="font-semibold leading-tight group-hover:text-amber-500 transition">{item.venue_name || item.name}</h3>
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
          {item.party_size_min && item.party_size_max && (
            <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> Parties of {item.party_size_min}–{item.party_size_max}</span>
          )}
          {item.city && (
            <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {cityName(item.city)}</span>
          )}
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2">
          {item.short_description || item.tagline}
        </p>
        {(item.seating_areas || []).length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {item.seating_areas.slice(0, 3).map(a => (
              <span key={a} className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-300">{a}</span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}
